"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import ArrowIcon from "@/components/icons/arrow";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { CarouselItem } from "@/types/dashboard";

const TIMER_DURATION = 10_000;
const TICK_INTERVAL = 50;
const MAX_PROGRESS = 100;
const DEGREES_FULL = 360;

const Carousel = ({ items }: { items: CarouselItem[] }) => {
  const [activeItem, setActiveItem] = useState(0);
  const [progress, setProgress] = useState(0);

  const its = useMemo(
    () =>
      (items || []).map((item) => {
        if (item.referenceType === "banner") {
          return {
            actionText: item.banner?.buttonText || "Devamı",
            actionUrl: item.banner?.actionUrl || "#",
            coverImage: item.banner?.smallImage,
            id: item.banner?.id || Math.random().toString(),
            lead: item.banner?.subtitle || "",
            thumbnailUrl: item.banner?.largeImage,
            title: item.banner?.title || "",
          };
        }
        return {
          actionText: "Devamı",
          actionUrl: item.content?.slug ? `/${item.content.slug}` : "#",
          coverImage: item.content?.coverImageUrl,
          id: item.content?.id || Math.random().toString(),
          lead: item.content?.lead || "",
          thumbnailUrl: item.content?.coverImageUrl || item.content?.thumbnailUrl,
          title: item.content?.title || "",
        };
      }),
    [items],
  );

  // Keep a stable ref to latest its so the interval closure never stales
  const itsRef = useRef(its);
  itsRef.current = its;

  // When set, the next interval tick will jump to this index and reset the timer
  const pendingSelectRef = useRef<number | null>(null);

  useEffect(() => {
    let startTime = Date.now();
    setProgress(0);

    const interval = setInterval(() => {
      if (pendingSelectRef.current !== null) {
        setActiveItem(pendingSelectRef.current);
        pendingSelectRef.current = null;
        startTime = Date.now();
        setProgress(0);
        return;
      }

      const elapsed = Date.now() - startTime;
      const next = Math.min((elapsed / TIMER_DURATION) * MAX_PROGRESS, MAX_PROGRESS);
      setProgress(next);

      if (next >= MAX_PROGRESS) {
        setActiveItem((prev) => (prev + 1) % itsRef.current.length);
        startTime = Date.now();
        setProgress(0);
      }
    }, TICK_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  const handleSelect = (index: number) => {
    pendingSelectRef.current = index;
  };

  const degrees = (progress / MAX_PROGRESS) * DEGREES_FULL;

  return (
    <div className="aspect-video z-0 w-full h-full relative">
      {its.map((item, index) => (
        <div
          className={cn(
            "absolute inset-0 rounded-2xl transition-opacity duration-500 ease-in-out",
            activeItem === index
              ? "opacity-100 z-10"
              : "opacity-0 z-0 pointer-events-none",
          )}
          key={item.id}
        >
          {item.coverImage ? (
            <Image
              alt={item.title || "Carousel image"}
              className="object-cover rounded-2xl"
              fill={true}
              priority={index === 0}
              sizes="(max-width: 640px) 100vw, 75vw"
              src={item.coverImage}
            />
          ) : (
            <div className="w-full h-full bg-gray-300 rounded-2xl" />
          )}
          <div
            className="absolute inset-0 rounded-2xl"
            style={{
              background:
                "radial-gradient(circle, rgba(0,0,0,0) 0%, rgba(0,0,0,0.5) 100%)",
            }}
          />
          <div className="relative z-10 flex h-full flex-col justify-between px-4 py-6 sm:px-5 sm:py-8 xl:px-8 xl:py-12">
            <div className="md:w-102 lg:w-130 shrink-0 flex flex-col justify-center gap-4">
              <h2
                className={cn(
                  "text-[22px] leading-8 font-bold text-white",
                  "sm:text-3xl sm:leading-10 lg:text-5xl lg:leading-14",
                )}
              >
                {item.title}
              </h2>
              <p className="text-sm leading-5 font-medium text-white line-clamp-2">
                {item.lead}
              </p>
              <Link href={item.actionUrl || "#"}>
                <Button className="w-fit hover:bg-brand-blue-500" type="button">
                  {item.actionText}
                  <ArrowIcon className="stroke-white" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      ))}
      <div
        className="absolute bottom-4 md:bottom-8 left-4 md:left-8 z-20 flex flex-row gap-2"
        id="carousel-indicators"
      >
        {its.map((item, index) => (
          <div
            className={cn(
              "md:size-18 bg-white rounded-lg cursor-pointer",
              "size-8",
            )}
            // biome-ignore lint/suspicious/noArrayIndexKey: its ok
            key={index}
            onClick={() => handleSelect(index)}
            role="none"
          >
            <div
              className={cn(
                "h-full w-full rounded-lg",
                activeItem === index && "p-0.5 md:p-1",
              )}
              style={
                activeItem === index
                  ? {
                      backgroundImage: `conic-gradient(from 0deg, var(--brand-blue) 0deg ${degrees}deg, #FFFFFF ${degrees}deg ${DEGREES_FULL}deg)`,
                    }
                  : {}
              }
            >
              <div className="relative h-full w-full overflow-hidden rounded-md">
                <Image
                  alt={item.title || "Thumbnail image"}
                  className="object-cover"
                  fill={true}
                  sizes="72px"
                  src={item.thumbnailUrl || "/globe.svg"}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Carousel;
